// Canvas 7: Capability and Change. Two to four roles, what each does today and needs, the gap type, one adoption lever and a first rehearsal,
// then the change owner and the One Story Check. Pulls from Canvases 1 to 6 and Case Card C. Feeds 8 and 9 (the frontline sentence feeds Canvas 9).
import { makeDef, G, text, long, pick, multi, calc, fields, table, pull, note, reference } from './base.js';
import { OPT } from '../spine.js';
import { has, S, T, rows, listOf } from '../helpers.js';

const ROLE_IDS = ['r1', 'r2', 'r3', 'r4'];
const GAP_HINTS = { Skill: 'can they?', Trust: 'will they?', Structure: 'who owns it?' };
const GAP_OPTS = OPT.gaptype.map((v) => ({ v, label: v, hint: GAP_HINTS[v] }));

// ---------------------------------------------------------------- reference panels (the back page of the printed canvas)
const QUESTIONS = [
  [
    'Can they?',
    'The skills for the new task, and the judgement to know when to rely on the tool and when to override it',
    'Skill',
    'Canvas 1, People and Governance; Canvas 5, roles',
  ],
  [
    'Will they?',
    'Whether they find it useful and easy, and whether their trust fits what the tool can really do',
    'Trust',
    'Canvas 2, the human role at each trust moment; Canvas 6, handover triggers',
  ],
  [
    'Who owns it?',
    'A named owner for the change in that role, and a path that exists: the time, the tools and the permission to use it',
    'Structure',
    'Canvas 5, operating model; the sponsor on your Case Card',
  ],
];
const LEVERS = [
  ['training', 'Practice on real scenarios, role by role, with time to try the tool on live work', 'A one-hour briefing for everyone'],
  ['incentives', 'The measures and recognition people are judged on reward the new way of working', 'A target that still rewards the old way'],
  ['leadership modelling', 'A named leader uses the tool in front of the team, first', 'An announcement that leaders support it'],
  ['feedback loops', "Staff raise what is wrong, see it fixed, and can adjust the tool's output", 'A suggestion box nobody answers'],
];
const LEVER_NOTE =
  'Choose one lever for each role. A memo is not a lever. If the gap is Structure, fix the path first: name the owner and give the time, then choose a lever.';
const ROLES_KEY = [
  [
    'Roles (Step 1)',
    'A role this use case touches on an ordinary day. Start from the human roles on Canvas 2 and the owners on Canvas 5. Two to four roles. ' +
      'A row is filled when it has a role, today, needed, at least one gap type, a lever and an owner.',
  ],
];
const ROW_KEY = [
  [
    'First rehearsal, and Repeat when',
    'Each S, D or R moment on Canvas 2 has a handover trigger on Canvas 6: low confidence, the customer asks, or an S, D or R moment. ' +
      'Each such moment becomes one rehearsal: a named scenario that the role practises before launch, covering the three handover states ' +
      '(AI continues; AI clarifies; transfers to a human). A role with no S, D or R moment may have a rehearsal of its own new task, or none. ' +
      'Repeat when: when the rehearsal runs again, for example after the first visible error. One session is not a schedule.',
  ],
  [
    'Two kinds of owner',
    "Owner (one for each row): the role accountable for closing that role's gap. Change owner (one for the whole canvas): the role " +
      'accountable for the change as a whole, who answers the Frontline\'s question "who asked me?". Both are roles, never names. ' +
      'They may be the same role.',
  ],
  [
    'This tool cannot',
    "One line, in words a colleague would say aloud, that names what the tool cannot do. Say it before the tool's first visible error, not after.",
  ],
];
const STORY_HEAD = ['Example', 'Told to the CFO (Canvas 4)', 'Released time goes to', 'Told to the frontline (Canvas 7)', 'Check 4'];
const STORY_FAIL = [['A story that fails (invented for teaching)', 'Six fewer branch roles by year two', 'Not said', '"Nothing changes for you."', 'Go back']];
const STORY_KEY = [
  [
    'How to run it',
    '1 Copy the efficiency claim from Canvas 4, with its tag (M, E or G). 2 Write where the released time or capacity goes. ' +
      '3 Write the one sentence you will say to the frontline. 4 Read the two side by side. Do they tell one story? Mark Pass or Go back. ' +
      'If Canvas 4 makes a headcount claim, it must appear in the frontline sentence.',
  ],
  [
    'What Go back means',
    'Go back to Canvas 4, the source, and decide what is true. Then rewrite both sides so that they agree. Never soften the frontline ' +
      'sentence to hide a claim you made to the CFO, and never edit Canvas 7 to make the check pass.',
  ],
  [
    'Three tests for your frontline sentence',
    'Would you say it, unchanged, with your CFO in the room? Does it say where the released time goes? ' +
      'Does it say what happens to roles, for the period you can actually speak for?',
  ],
];

// ---------------------------------------------------------------- local helpers
const fx = (up, n) => (up && up[n] && up[n].d && up[n].d.facts) || {};
const uniq = (list) => [...new Set(list.filter(has))];
const rolesIn = (v) => rows(v, 'roles').filter((r) => has(r.role));

// "Branch re-keying minutes per application" reads in a sentence as "Branch re-keying time"
const shortMeasure = (m) =>
  String(m || '')
    .replace(/\([^)]*\)/g, ' ')
    .split(',')[0]
    .split(/\s+per\s+/i)[0]
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b(minutes|hours|seconds)$/i, 'time');

// the efficiency claim of Canvas 4 (its AI and efficiency row): the measure and the change, with the tag
function claimOf(up) {
  const r = (fx(up, 4).rows || {}).efficiency;
  if (!r || !has(r.measure)) return null;
  const change = has(r.change) ? String(r.change).trim() : '';
  const tag = has(r.tag) ? String(r.tag).trim() : '';
  const full = `${String(r.measure).trim()}${change ? `: ${change}` : ''}`;
  const phrase = `${shortMeasure(r.measure)}${change ? ` ${change}` : ''}`;
  return { line: `${full}${tag ? ` (${tag})` : ''}`, phrase: `${phrase}${tag ? ` (${tag})` : ''}` };
}

// the Canvas 6 triggers: the facts when Canvas 6 publishes them, otherwise the raw answers of Canvas 6
function triggersOf(up) {
  const f = fx(up, 6);
  if (Array.isArray(f.triggers) && f.triggers.some(has)) return f.triggers.map((t) => String(t || '').trim());
  const v6 = (up && up[6] && up[6].v) || {};
  return ['low', 'ask', 'sdr'].map((id) => S(v6, `trig.${id}.text`));
}

// the human roles named on Canvas 2 (a stage with "None needed" names no role)
const humanRoles = (up) => uniq((fx(up, 2).stages || []).map((s) => String(s.human || '').trim()).filter((h) => !/^none\b/i.test(h)));
const dataOwners = (up) => uniq((fx(up, 5).dataItems || []).map((d) => String(d.owner || '').trim()));

const roleRows = (up, v) => rolesIn(v).map((r) => ({ id: r.id, label: r.role }));

// ---------------------------------------------------------------- NorthStar (master spec section 6; the printed canvas)
const NORTHSTAR = {
  ...T('roles', [
    { role: 'Branch staff in the pilot region' },
    { role: 'Service officers who call on declines' },
    { role: 'CX product owners (two, trained in Phase 1)' },
    { role: 'Risk analysts who validate the model each month' },
  ]),
  ...T('need', [
    {
      today: 'Re-key applicant data from documents into the application form',
      needed: 'Check the pre-filled data with the customer, correct it, and say plainly that the customer can decline the pre-fill',
      gapTypes: 'Skill',
    },
    {
      today: 'Call customers on some declines, with no reason to give',
      needed: 'Explain the plain-language reason, and judge when to overrule or pass the case on (a D and R moment)',
      gapTypes: 'Skill,Trust',
    },
    {
      today: 'The role does not exist yet; nobody owns a full journey',
      needed: 'Own the journey outcome and read the Canvas 4 measures',
      gapTypes: 'Structure',
    },
    {
      today: 'See projects only after launch',
      needed: 'Validate the model each month, and review a decline against its plain-language reason',
      gapTypes: 'Skill,Trust',
    },
  ]),
  ...T('lev', [
    {
      lever: 'training',
      rehearsal: 'Correcting a wrong pre-fill with the customer',
      repeat: 'After the first wrong pre-fill a customer reports',
      owner: 'Head of Retail Banking',
    },
    {
      lever: 'leadership modelling',
      rehearsal: 'The decline handover, three states',
      repeat: 'After any decline that is overturned on review',
      owner: 'Head of Retail Banking',
    },
    { lever: 'incentives', owner: 'Head of CX' },
    { lever: 'feedback loops', owner: 'Head of Risk' },
  ]),
  cannot: 'Tell whether a pre-filled answer is still true: the customer and the officer check it',
  'change.owner': 'Head of Retail Banking',
  released: 'Customers who need a person',
  frontline: 'Pre-fill takes the re-keying off your desk, and the time goes to customers who need a person. No branch role is cut in Phase 1.',
  consistent: 'Yes',
  'so.reviewer': 'Frontline',
  'so.objection': 'What happens to my job when this works?',
  'so.evidence': 'The One Story Check and the frontline sentence: Canvas 7',
  'so.owner': 'Head of Retail Banking',
  'so.status': 'Answered',
};

export default makeDef(7, {
  short: 'Capability',
  purpose: 'Name the roles this use case changes, find what each needs, choose how to build adoption, and check the frontline hears the same story as the CFO.',
  howTo: [
    'Work one role at a time. A role, never a name. Start from the roles already named in your pack.',
    'For each role, write what they do today and what they need, then tick the gap: Skill (can they?), Trust (will they?) or Structure (who owns it?).',
    'Choose one adoption lever for each role and name a first rehearsal from a Canvas 6 trigger. A memo is not a lever.',
    'Name the change owner, then write one sentence for the frontline. It must tell the same story as the efficiency claim on Canvas 4.',
  ],
  results: [
    { id: 'roles', label: 'Roles', rule: 'Count the roles you named in Step 1.' },
    { id: 'owners', label: 'Roles with a named owner', rule: 'Count the roles with an owner in Step 3.' },
    { id: 'gaps', label: 'Gap types ticked', rule: 'Count the roles ticked for Skill, for Trust and for Structure in Step 2.' },
    { id: 'rehearsals', label: 'Roles with a first rehearsal', rule: 'Count the roles with a first rehearsal in Step 3.' },
    { id: 'changeOwner', label: 'Change owner', rule: 'From Step 4.' },
    { id: 'check4', label: 'Check 4: One Story', rule: 'Yes is Pass. No, or no efficiency claim on Canvas 4, is Go back.' },
  ],
  steps: [
    {
      hint:
        'Name two to four roles this use case touches on an ordinary day. A role, never a name. ' +
        'Start from the human roles on Canvas 2 and the owners on Canvas 5.',
      blocks: [
        pull('p1', 'From your pack', [
          { id: 'top', label: 'Top pick', ref: 'Canvas 3', no: 3, get: (up) => (fx(up, 3).topPick || {}).name || null },
          { id: 'sponsor', label: 'Sponsor', ref: 'Case Card C', no: 0, get: (up) => fx(up, 0).sponsor || null },
          {
            id: 'case',
            label: 'Use case tested on Canvas 6',
            ref: 'Canvas 6',
            no: 6,
            get: (up) => {
              const f = fx(up, 6);
              return has(f.useCase) ? `${f.useCase}${f.flagged ? ' (guardrail flag)' : ''}` : null;
            },
          },
          {
            id: 'humans',
            label: 'Human roles on Canvas 2',
            ref: 'Canvas 2',
            no: 2,
            get: (up) => humanRoles(up).join('; ') || null,
            note: 'The people at each trust moment and stage.',
          },
          {
            id: 'jowner',
            label: 'Journey owner on Canvas 5',
            ref: 'Canvas 5',
            no: 5,
            get: (up) => ((fx(up, 5).operating || {}).journeyOwner || '').trim() || null,
          },
          { id: 'downers', label: 'Data owners on Canvas 5', ref: 'Canvas 5', no: 5, get: (up) => dataOwners(up).join('; ') || null },
          {
            id: 'people',
            label: 'People and Governance',
            ref: 'Canvas 1',
            no: 1,
            get: (up) => {
              const p = (fx(up, 1).dims || {}).people;
              if (!p || p.level === null || p.level === undefined || p.level === '') return null;
              return `Level ${p.level}${p.levelName ? `, ${p.levelName}` : ''}${p.proof ? `: ${p.proof}` : ''}`;
            },
          },
        ]),
        table('roles', {
          rowLabel: 'Role',
          rows: { min: 2, max: 4, start: 2, label: 'Role' },
          pdf: 'table',
          columns: [
            text('role', 'Role this use case touches (a role, never a name)', {
              required: true,
              w: 4,
              placeholder: 'For example: Branch staff in the pilot region',
              guide: G(
                'Name a role this use case touches on an ordinary day. Start from the roles already named in your pack. Two to four roles.',
                'Branch staff in the pilot region',
                undefined,
                'A person, or a whole department. Write a role that one group of people holds.',
              ),
            }),
          ],
        }),
        reference('Roles, rehearsals, owners and "This tool cannot"', ['Topic', 'What it means'], ROLES_KEY),
      ],
    },
    {
      hint:
        'For each role, write what they do now that this touches, and what they need for AICX, including judgement at S, D, R moments. ' +
        'Then tick the gap type: Skill (can they?), Trust (will they?) or Structure (who owns it?).',
      blocks: [
        table('need', {
          rowLabel: 'Role',
          rows: { from: roleRows },
          pdf: 'cards',
          columns: [
            long('today', 'Today: what they do now that this touches', {
              required: true,
              w: 3,
              rows: 2,
              guide: G(
                'Write what they do now that this use case touches, in a line.',
                'Re-key applicant data from documents into the application form',
                undefined,
                'Describing the future. Write what happens today.',
              ),
            }),
            long('needed', 'Needed for AICX, including judgement at S, D, R moments', {
              required: true,
              w: 3,
              rows: 2,
              guide: G(
                'Write what they must do or judge once AICX is live, including when to rely on the tool and when to override it.',
                'Check the pre-filled data with the customer, correct it, and say plainly that the customer can decline the pre-fill',
                undefined,
                'Listing training on the tool. Describe the new task and the judgement it needs.',
              ),
            }),
            multi('gapTypes', 'Gap type', GAP_OPTS, {
              required: true,
              w: 2,
              guide: G(
                'Tick each gap that applies to this role. Skill: can they? Trust: will they? Structure: who owns it?',
                'Skill for branch staff; Skill and Trust for service officers; Structure for CX product owners.',
                'Skill: the skills for the new task, and the judgement to rely on the tool or override it. ' +
                  'Trust: they find it useful and easy, and their trust fits what the tool can really do. ' +
                  'Structure: a named owner for the change, and a path that exists: the time, the tools and the permission.',
                'Calling every gap a Skill gap. Ask also: will they, and who owns it?',
              ),
            }),
          ],
        }),
        reference('Three questions for every role (AICX working rule)', ['Ask', 'It covers', 'Gap type to tick', 'Where the answer comes from'], QUESTIONS),
      ],
    },
    {
      hint:
        'Choose one lever for each role (a memo is not a lever), and name a first rehearsal from a Canvas 6 trigger. ' +
        'Say when it repeats and who owns it. Then write one thing the tool cannot do.',
      blocks: [
        pull('p3', 'From Canvas 6', [
          {
            id: 'triggers',
            label: 'Canvas 6 triggers: rehearse these',
            ref: 'Canvas 6',
            no: 6,
            get: (up) =>
              triggersOf(up)
                .map((t, i) => (has(t) ? `${i + 1}: ${t}` : ''))
                .filter(Boolean)
                .join('\n') || null,
          },
        ]),
        table('lev', {
          rowLabel: 'Role',
          rows: { from: roleRows },
          pdf: 'cards',
          columns: [
            pick('lever', 'Adoption lever', OPT.lever, {
              required: true,
              w: 2,
              guide: G(
                'Choose the one lever that closes this role\'s gap. A memo is not a lever.',
                'training for branch staff; leadership modelling for service officers.',
                'training: practice on the real task. incentives: what the role is measured and rewarded on. leadership modelling: a leader goes first, ' +
                  'in front of the team. feedback loops: the team reports what is not useful or not easy, and sees it acted on.',
                'A one-hour briefing or an announcement. If the gap is Structure, name the owner and give the time first, then choose a lever.',
              ),
            }),
            text('rehearsal', 'First rehearsal (from a Canvas 6 trigger)', {
              w: 3,
              guide: G(
                'Name one scenario this role will practise before launch, taken from a Canvas 6 trigger. ' +
                  'A role with no S, D or R moment may have a rehearsal of its own new task, or none.',
                'Correcting a wrong pre-fill with the customer',
                'Cover the three handover states: AI continues, AI clarifies, transfers to a human.',
                'A general training day. Name one scenario.',
              ),
            }),
            text('repeat', 'Repeat when', {
              w: 3,
              guide: G(
                'Say when the rehearsal runs again, for example after the first visible error.',
                'After the first wrong pre-fill a customer reports',
                undefined,
                'One session is not a schedule.',
              ),
            }),
            text('owner', 'Owner (a role)', {
              required: true,
              w: 2,
              guide: G(
                "The role accountable for closing this role's gap. A role, never a name.",
                'Head of Retail Banking',
                undefined,
                'Naming the role that is being trained. Name the one accountable for the change in it.',
              ),
            }),
          ],
        }),
        note(LEVER_NOTE, 'rule'),
        fields([
          long('cannot', 'This tool cannot', {
            w: 4,
            rows: 2,
            placeholder: 'One thing the tool cannot do, in plain words',
            guide: G(
              "Write one line, in words a colleague would say aloud, that names what the tool cannot do. Say it before the tool's first visible error.",
              'Tell whether a pre-filled answer is still true: the customer and the officer check it',
              'It is the first line of the training, said before the first rehearsal.',
              'Hiding the limit until the first error. Say it first.',
            ),
          }),
        ]),
        reference('Four adoption levers (AICX working rule)', ['Lever', 'What it looks like in practice', 'What it is not'], LEVERS),
        reference('Rehearsals, owners and "This tool cannot"', ['Topic', 'What it means'], ROW_KEY),
      ],
    },
    {
      hint:
        'Name the one role that owns the change. Read the efficiency claim on Canvas 4, then write where the released time or capacity goes. ' +
        'If Canvas 4 claims fewer people, say so here.',
      blocks: [
        pull('p4', 'From Canvas 4', [
          {
            id: 'claim',
            label: 'Efficiency claim, with its tag',
            ref: 'Canvas 4',
            no: 4,
            get: (up) => (claimOf(up) || {}).line || null,
            note: 'What the CFO has been told.',
          },
        ]),
        fields([
          text('change.owner', 'Change owner (one role that owns the change)', {
            required: true,
            w: 4,
            placeholder: 'A role, not a name',
            guide: G(
              'Name the one role accountable for the change as a whole. It answers the frontline question "who asked me?". It may be the same role as a row owner.',
              'Head of Retail Banking',
              undefined,
              'A committee, or "the team". Name one role.',
            ),
          }),
          long('released', 'Released time goes to', {
            required: true,
            w: 4,
            rows: 2,
            guide: G(
              'Say where the hours or capacity that the claim releases will go. If Canvas 4 claims fewer people, say so here.',
              'Customers who need a person',
              undefined,
              'Leaving it blank, or writing "efficiency". The frontline will ask where their time goes.',
            ),
          }),
        ]),
      ],
    },
    {
      hint:
        'Write the one sentence you will say to the frontline. Then read it beside the Canvas 4 claim: do the CFO and the frontline hear one story?',
      blocks: [
        pull('p5', 'Told to the CFO', [
          { id: 'cfo', label: 'Efficiency claim on Canvas 4', ref: 'Canvas 4', no: 4, get: (up) => (claimOf(up) || {}).line || null },
        ]),
        fields([
          long('frontline', 'What we tell the frontline (one sentence)', {
            required: true,
            w: 4,
            rows: 3,
            placeholder: 'What changes for them, where the released time goes, and what happens to roles',
            guide: G(
              'Write the one sentence you will say to the frontline: what changes for them, where the released time goes, and what happens to roles.',
              'Pre-fill takes the re-keying off your desk, and the time goes to customers who need a person. No branch role is cut in Phase 1.',
              'Three tests. Would you say it, unchanged, with your CFO in the room? Does it say where the released time goes? ' +
                'Does it say what happens to roles, for the period you can actually speak for?',
              'Softening the sentence to hide a claim you made to the CFO.',
            ),
          }),
          pick('consistent', 'Consistent with the Canvas 4 claim?', OPT.yesno, {
            required: true,
            w: 2,
            guide: G(
              'Read the Canvas 4 claim and your sentence side by side. Do they tell one story? Yes: the CFO and the frontline hear one story.',
              'Yes',
              'Yes is Pass. No, or no efficiency claim on Canvas 4, is Go back. A headcount claim on Canvas 4 must appear in the sentence.',
              'Editing Canvas 7 to make the check pass. Go back to Canvas 4 and decide what is true.',
            ),
          }),
          calc('check4', 'Check 4: One Story Check', {
            w: 2,
            rule: 'Yes is Pass. No, or no efficiency claim on Canvas 4, is Go back',
            guide: G(
              'Worked out for you: Pass or Go back.',
              'Pass',
              'Go back means correct the claim on Canvas 4, or log the conflict in the Ledger as Open.',
              'Softening the frontline sentence to hide a claim you made to the CFO.',
            ),
          }),
        ]),
        reference('The One Story Check (AICX working rule)', STORY_HEAD, STORY_FAIL),
        reference('How to run the check', ['Topic', 'What it means'], STORY_KEY),
      ],
    },
  ],
  derive(v, up) {
    const out = { calc: {}, results: {}, evidence: [], warnings: [], blockers: [] };
    const roles = rolesIn(v).map((r) => ({
      id: r.id,
      role: r.role,
      today: S(v, `need.${r.id}.today`),
      needed: S(v, `need.${r.id}.needed`),
      gapTypes: OPT.gaptype.filter((g) => listOf(S(v, `need.${r.id}.gapTypes`)).includes(g)),
      lever: S(v, `lev.${r.id}.lever`),
      rehearsal: S(v, `lev.${r.id}.rehearsal`),
      repeat: S(v, `lev.${r.id}.repeat`),
      owner: S(v, `lev.${r.id}.owner`),
    }));
    const n = roles.length;
    const gapCounts = Object.fromEntries(OPT.gaptype.map((g) => [g, roles.filter((r) => r.gapTypes.includes(g)).length]));
    const anyGap = roles.some((r) => r.gapTypes.length);
    const rehearsals = roles.filter((r) => r.rehearsal).length;
    const changeOwner = S(v, 'change.owner');
    const released = S(v, 'released');
    const frontline = S(v, 'frontline');
    const consistent = S(v, 'consistent');
    const claim = claimOf(up);

    // Check 4, the One Story Check: blank until the three answers are in; no claim on Canvas 4 is Go back
    let check = '';
    if (has(released) && has(frontline) && has(consistent)) check = claim && consistent === 'Yes' ? 'Pass' : 'Go back';

    out.calc.check4 = check;
    out.results = {
      roles: n ? `${n} of 4` : '',
      owners: n ? `${roles.filter((r) => r.owner).length} of ${n}` : '',
      gaps: anyGap ? OPT.gaptype.map((g) => `${g} ${gapCounts[g]}`).join(', ') : '',
      rehearsals: n ? `${rehearsals} of ${n}` : '',
      changeOwner: n ? changeOwner || 'None named' : '',
      check4: check,
    };

    if (!claim) {
      out.warnings.push({
        step: 's4',
        text: 'Canvas 4 has no efficiency claim yet. Write the efficiency story there first: this check compares it with the frontline sentence.',
      });
    } else if (check === 'Go back') {
      out.warnings.push({
        step: 's5',
        text: 'Go back to Canvas 4. The CFO and the frontline are hearing two stories. Correct the efficiency claim there, or log the conflict in the Ledger as Open.',
      });
    }
    if (n > 0 && rehearsals === 0 && roles.some((r) => r.lever)) {
      out.warnings.push({ step: 's3', text: 'No role has a first rehearsal yet. Rehearse your Canvas 6 triggers, role by role.' });
    }

    out.facts = {
      roles,
      gapCounts,
      changeOwner,
      claim: claim ? claim.phrase : '',
      frontline,
      consistent,
      // extras for later canvases
      check4: check,
      released,
      cannot: S(v, 'cannot'),
    };
    return out;
  },
  NORTHSTAR,
});
